import { EmptyState, LinkAction, SectionCard } from '@atlas/ui-components'
import { Skeleton } from '@atlas/ui-primitives'
import { ArrowRight } from 'lucide-react'
import type { CampaignListItem } from './campaign-list-card.types'
import { CampaignListRow } from './campaign-list-row'

/** The card's default heading. Shared with a host's own skeleton so the two cannot diverge. */
export const CAMPAIGN_LIST_TITLE = 'Campaigns'

/**
 * The card's body while its reads run: three rows on the rhythm a real one sits on.
 *
 * Exported so a host's first-read skeleton stands the section in the same way, rather than as
 * generic text lines that then reflow into rows.
 */
export function CampaignListBodySkeleton({ label }: { label?: string }) {
	return (
		<>
			{label ? <span className="sr-only">{label}</span> : null}
			<ul aria-busy="true" className="space-y-2">
				{Array.from({ length: 3 }, (_, i) => (
					<li key={i}>
						<Skeleton className="h-14 w-full" />
					</li>
				))}
			</ul>
		</>
	)
}

export interface CampaignListCardProps {
	campaigns: CampaignListItem[]
	/** What this surface calls the section - two surfaces name the same list differently. */
	title?: string
	/** The campaigns-list destination for the heading action. Omit it and no action renders. */
	viewAllHref?: string
	/** Called instead of navigating, so the host performs in-app navigation. */
	onViewAll?: () => void
	onOpenCampaign: (campaignId: string) => void
	isLoading?: boolean
	/**
	 * Whether the read failed. Distinct from an empty result: "no campaigns apply" is an
	 * answer a reader acts on, and reporting it for a read that never landed would clear a
	 * context of campaigns the service was simply unable to state.
	 */
	isError?: boolean
	/** What an empty result means here - the host knows whether its filters narrowed it away. */
	emptyLabel?: string
}

/**
 * The campaigns applying to whatever the host resolved for, as a section card.
 *
 * One component for every surface that lists campaigns: they render the same rows from the
 * same shape and only name themselves differently, and two copies would drift on the first
 * change to either.
 *
 * Navigation stays with the host: a row emits an id, and the heading action is an anchor the
 * host intercepts.
 */
export function CampaignListCard({
	campaigns,
	title = CAMPAIGN_LIST_TITLE,
	viewAllHref,
	onViewAll,
	onOpenCampaign,
	isLoading,
	isError,
	emptyLabel = 'No campaigns apply here.',
}: CampaignListCardProps) {
	return (
		<SectionCard
			title={title}
			count={isLoading || isError ? undefined : campaigns.length}
			action={
				viewAllHref ? (
					<LinkAction href={viewAllHref} onNavigate={onViewAll} aria-label="All campaigns">
						<ArrowRight aria-hidden className="h-4 w-4" />
					</LinkAction>
				) : null
			}
		>
			{isLoading ? (
				<CampaignListBodySkeleton label="Loading the campaigns…" />
			) : isError ? (
				<p className="text-ds-sm leading-5 text-ds-text-muted">
					Could not load the campaigns for this context. Please retry.
				</p>
			) : campaigns.length === 0 ? (
				<EmptyState title={emptyLabel} />
			) : (
				<ul className="space-y-2">
					{campaigns.map((campaign) => (
						<li key={campaign.id}>
							<CampaignListRow campaign={campaign} onActivate={onOpenCampaign} />
						</li>
					))}
				</ul>
			)}
		</SectionCard>
	)
}
